import { defineStore, storeToRefs } from 'pinia'
import { useQuery, useMutation } from '@tanstack/vue-query'
import { computed, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import api from '@/services/axiosApi'
import * as jsondiffpatch from 'jsondiffpatch'
import { Team } from '@/types/team'
import { shouldAutosaveTeamChange } from './teamAutosaveSync'
import { requestUuid } from '@/services/uuid'
import { useNotificationStore } from './notificationStore'
import { useAuthStore } from './authStore'
import { useConfigStore } from './configStore'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@/services/analytics'

export type TeamCreateInput = Pick<Team, 'name' | 'website'>
export type TeamCreateAfterCreate = 'dashboard' | 'onboarding'

export interface TeamCreateWarning {
  code: string
  message: string
}

export type TeamCreateBillingModel = 'legacy' | 'per_team'

export interface TeamCreateResult {
  team: Team
  warning: TeamCreateWarning | null
  replayed: boolean
  requiresBillingSetup: boolean
  billingModel: TeamCreateBillingModel
  hasPaymentMethod: boolean
}

export interface TeamCreateOptions {
  afterCreate?: TeamCreateAfterCreate
  clientRequestId?: string
}

type TeamCreateResponse = {
  team: Team
  warning?: TeamCreateWarning | null
  replayed?: boolean
  requires_billing_setup?: boolean
  billing_model?: TeamCreateBillingModel
  has_payment_method?: boolean
}

export const useTeamStore = defineStore('team', () => {
  const doesOwnCurrentTeam = ref(false) as Ref<boolean>
  const authStore = useAuthStore()
  const configStore = useConfigStore()
  const notificationStore = useNotificationStore()
  const { currentTeam, currentTeamPid, fullResponse, config, teamMembers, snapshots, isApplyingRemoteTeam } =
    storeToRefs(configStore)
  const router = useRouter()
  const { user } = storeToRefs(authStore)

  // Detect if we're in demo mode
  const isDemo = computed(() => currentTeamPid.value === 'demo')

  // Query for authenticated user's real teams - NEVER affected by demo mode
  // This prevents race conditions during registration where FakeLayout sets demo mode
  // but we need to fetch the user's actual teams for post-login redirect
  const userTeamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get('/api/teams').then((res) => res.data),
    enabled: computed(() => !!user.value)
  })
  const demoTeamsQuery = useQuery({
    queryKey: ['demo-teams'],
    queryFn: () => api.get('/api/demo/teams').then((res) => res.data),
    enabled: computed(() => isDemo.value && !user.value)
  })

  // Return real teams for authenticated users, demo teams only for unauthenticated demo viewers
  const teams = computed(() => {
    if (user.value) {
      return userTeamsQuery.data.value || []
    }
    return demoTeamsQuery.data.value || []
  })

  watch(
    () => currentTeam.value,
    (is, was) => {
      if (!is) {
        return
      }

      const diff = jsondiffpatch.diff(was, is) as Record<string, unknown> | null | undefined
      if (!shouldAutosaveTeamChange({
        diff,
        isApplyingRemoteTeam: isApplyingRemoteTeam.value
      })) {
        return
      }

      debouncedUpdateTeam(is)
    },
    { deep: true }
  )

  const updateTeamMutation = useMutation({
    mutationFn: (variables: { team: Team }) =>
      api.put(`/api/${variables.team.pid}/team`, {
        ...variables.team
      }),
    onSuccess: (_, variables) => {
      // Track team settings update
      recordAnalyticsEvent(AnalyticsEventTypes.SETTINGS_UPDATED, {
        settingType: 'team',
        teamId: variables.team.pid,
        teamName: variables.team.name,
        updatedFields: Object.keys(variables.team)
      })
    }
  })

  const deleteTeamMutation = useMutation({
    mutationFn: (variables: { team: Team }) =>
      api.delete(`/api/teams/${variables.team.pid}`),
    onSuccess: (_, variables) => {
      userTeamsQuery.refetch()
      configStore.configQuery.refetch()
      authStore.getUser(true)

      // Track team deletion
      recordAnalyticsEvent(AnalyticsEventTypes.TEAM_DELETED, {
        teamId: variables.team.pid,
        teamName: variables.team.name
      })
    }
  })

  const removeTeamMemberMutation = useMutation({
    mutationFn: (variables: { memberId: number; isInvited: boolean }) => {
      return api.delete(
        `api/${currentTeamPid.value}/team/members/${variables.memberId}`,
        {
          params: {
            invited: variables.isInvited ? '1' : '0'
          }
        }
      )
    },
    onSuccess: (_, variables) => {
      configStore.configQuery.refetch()

      // Track team member removal
      recordAnalyticsEvent(AnalyticsEventTypes.TEAM_MEMBER_REMOVED, {
        teamId: currentTeamPid.value,
        memberId: variables.memberId,
        wasInvited: variables.isInvited
      })
    }
  })

  const addTeamMemberMutation = useMutation({
    mutationFn: (memberData: { email: string; role: string }) =>
      api.post(`/api/${currentTeamPid.value}/team/members`, memberData),
    onSuccess: (_, variables) => {
      configStore.configQuery.refetch()

      // Track team member addition
      recordAnalyticsEvent(AnalyticsEventTypes.TEAM_MEMBER_ADDED, {
        teamId: currentTeamPid.value,
        memberEmail: variables.email,
        memberRole: variables.role
      })
    }
  })

  const deleteTeam = (team: Team) => {
    deleteTeamMutation.mutate({ team })
  }
  const removeTeamMember = () => {
    const { isError, error, isPending, mutate, mutateAsync } =
      removeTeamMemberMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }
  const addTeamMember = () => {
    const { isError, error, isPending, mutate, mutateAsync } =
      addTeamMemberMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }

  const debouncedUpdateTeam = useDebounceFn((newTeam: Team) => {
    updateTeamMutation.mutate({ team: newTeam })
  }, 2000)

  const create = async (team: TeamCreateInput, options: TeamCreateOptions = {}): Promise<TeamCreateResult> => {
    const clientRequestId = options.clientRequestId ?? requestUuid()
    const { data } = await api.post<TeamCreateResponse>('/api/teams', {
      ...team,
      client_request_id: clientRequestId
    })

    await userTeamsQuery.refetch()

    // Track team creation before we transition to the new dashboard.
    recordAnalyticsEvent(AnalyticsEventTypes.TEAM_CREATED, {
      teamId: data.team.pid,
      teamName: team.name,
      teamWebsite: team.website
    })

    // Seed the config store with the newly-created team immediately so the
    // dashboard/onboarding UI can render the new team's
    // `ai_context_status=pending|failed|ready` state without waiting for the
    // `/configs/current` fetch to land. Also clear team-scoped data from the
    // previously-open project so we don't momentarily render stale config,
    // members, or snapshots while the new team's queries hydrate.
    //
    // Guard this whole-object seed from the team deep-watch autosave: the
    // create response intentionally returns a thin shell (often
    // `ai_context=null`) that the background enrichment job / config refetch
    // fills in moments later. Posting this shell back would clobber the
    // richer server state we are waiting for.
    configStore.runWithoutTeamAutosave(() => {
      currentTeamPid.value = data.team.pid
      currentTeam.value = data.team
      fullResponse.value = null
      config.value = null
      teamMembers.value = []
      snapshots.value = []
    })

    const destination =
      options.afterCreate === 'dashboard'
        ? ROUTES.DASHBOARD
        : ROUTES.ONBOARDING

    await router.push({
      name: destination,
      params: { teamPid: data.team.pid },
      query: data.requires_billing_setup ? { billingSetup: '1' } : undefined
    })

    if (data.warning?.message) {
      notificationStore.addError(data.warning.message, false)
    }

    return {
      team: data.team,
      warning: data.warning ?? null,
      replayed: data.replayed === true,
      requiresBillingSetup: data.requires_billing_setup === true,
      billingModel: data.billing_model ?? 'per_team',
      hasPaymentMethod: data.has_payment_method === true
    }
  }

  // Compute isSuccess based on which query is active
  const isSuccess = computed(() => {
    if (user.value) {
      return userTeamsQuery.isSuccess.value
    }
    return demoTeamsQuery.isSuccess.value
  })

  return {
    teams,
    currentTeam,
    doesOwnCurrentTeam,
    isDemo,
    isSuccess,
    deleteTeam,
    create,
    removeTeamMember,
    addTeamMember
  }
})
